// -> IPortfolioDataManager (ProjectsController, MilestonesController, NotesController,
// RequestsController). BRD section 6 treats projects/milestones/notes/requests as four shapes of
// one volatility served by one Manager (see ProjectsController.cs's own header), so this file
// mirrors that rather than splitting into four.
import { http } from "./http";
import type {
  DeleteResult,
  EntityRef,
  MilestoneOrder,
  MilestoneReorderRequest,
  MilestoneSaveRequest,
  MilestoneDetail,
  NoteDetail,
  NoteSaveRequest,
  PagedResult,
  ProjectDetail,
  ProjectOwnerSummary,
  ProjectSummary,
  ProjectSaveRequest,
  RequestDetail,
  RequestSaveRequest,
  SaveResult,
} from "./generated/contracts";

export interface ListProjectsParams {
  readonly nameContains?: string;
  readonly teams?: readonly string[];
  readonly statuses?: readonly string[];
  readonly businessProjectId?: number;
  // D-303: sent only when true (hideCompleted?: false is dropped before the call), matching every
  // other optional filter on this list - the server's own ProjectQuery.hideCompleted defaults to
  // false, so omitting it is equivalent and keeps the "no filter active" request shape unchanged.
  readonly hideCompleted?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

export const portfolioDataApi = {
  listProjects: (params: ListProjectsParams = {}) =>
    http.get<PagedResult<ProjectSummary>>("/api/projects", { query: { ...params } }),

  getProject: (projectKey: string) => http.get<ProjectDetail>(`/api/projects/${projectKey}`),

  listOwners: () => http.get<ProjectOwnerSummary[]>("/api/projects/owners"),

  createProject: (request: ProjectSaveRequest) =>
    http.post<SaveResult<ProjectDetail>>("/api/projects", { body: request }),

  updateProject: (projectKey: string, request: ProjectSaveRequest) =>
    http.put<SaveResult<ProjectDetail>>(`/api/projects/${projectKey}`, { body: request }),

  deleteProject: (projectKey: string) => http.delete<DeleteResult>(`/api/projects/${projectKey}`),

  reorderMilestones: (projectKey: string, orderedMilestoneKeys: MilestoneReorderRequest["orderedMilestoneKeys"]) =>
    http.put<SaveResult<MilestoneOrder>>(`/api/projects/${projectKey}/milestones/order`, {
      body: orderedMilestoneKeys,
    }),

  createMilestone: (request: MilestoneSaveRequest) =>
    http.post<SaveResult<MilestoneDetail>>("/api/milestones", { body: request }),

  updateMilestone: (milestoneKey: string, request: MilestoneSaveRequest) =>
    http.put<SaveResult<MilestoneDetail>>(`/api/milestones/${milestoneKey}`, { body: request }),

  deleteMilestone: (milestoneKey: string) => http.delete<DeleteResult>(`/api/milestones/${milestoneKey}`),

  createNote: (request: NoteSaveRequest) => http.post<SaveResult<NoteDetail>>("/api/notes", { body: request }),

  updateNote: (noteKey: string, request: NoteSaveRequest) =>
    http.put<SaveResult<NoteDetail>>(`/api/notes/${noteKey}`, { body: request }),

  deleteNote: (noteKey: string) => http.delete<DeleteResult>(`/api/notes/${noteKey}`),

  createRequest: (request: RequestSaveRequest) =>
    http.post<SaveResult<RequestDetail>>("/api/requests", { body: request }),

  updateRequest: (requestKey: string, request: RequestSaveRequest) =>
    http.put<SaveResult<RequestDetail>>(`/api/requests/${requestKey}`, { body: request }),

  deleteRequest: (requestKey: string) => http.delete<DeleteResult>(`/api/requests/${requestKey}`),
};

/** Matches ProjectsController.DeleteAsync's EntityRef shape, for callers building one directly. */
export function projectRef(projectKey: string): EntityRef {
  return { entityType: "Project", entityKey: projectKey };
}
